import { createSlice } from "@reduxjs/toolkit";
import {timeSince} from "./utils";

const initialState = {
    workspaceId: null,
    workspaceMembership: null,
    workspaceMemberships: [],
    tasks: [],
    projects: [],
    goals: [],
    teams: [],
    customers: [],
    products: [],
    plans: [],
    orders: [],
    invoices: [],
    billingProfiles: [],
    creditWallets: [],
    creditLedgerEntries: [],
    manualPaymentRequests: [],
    promotions: [],
    coupons: [],
    couponRedemptions: [],
    referralPrograms: [],
    referralCodes: [],
    referralAttributions: [],
    referralRewards: [],
    users: [],
    lastEvent: null,
};

function normalizeTask(item) {
    if (!item || typeof item !== "object") return item;

    const dueDateUnix = item.dueDate
        ? new Date(item.dueDate).getTime() / 1000
        : null;
    const timeRemain = item.dueDate
        ? timeSince(dueDateUnix, "YYYY/MM/DD", {thisWeekThreshold: true})
        : null;

    return item.timeRemain === timeRemain && item.dueDateUnix === dueDateUnix
        ? item
        : {...item, timeRemain, dueDateUnix};
}

function upsertByDocumentId(items, item, normalizeItem = (value) => value) {
    if (!item) return items;
    const normalizedItem = normalizeItem(item);
    const key = normalizedItem.documentId ?? normalizedItem.id;
    const index = items.findIndex((current) => (current.documentId ?? current.id) === key);
    if (index >= 0) {
        const next = [...items];
        next[index] = {...next[index], ...normalizedItem};
        return next;
    }
    return [...items, normalizedItem];
}

function normalizeByDocumentId(items, normalizeItem = (value) => value) {
    if (!Array.isArray(items)) return [];
    return items.reduce((acc, item) => upsertByDocumentId(acc, item, normalizeItem), []);
}

function mergeByDocumentId(items, incomingItems, normalizeItem = (value) => value) {
    if (!Array.isArray(incomingItems) || !incomingItems.length) return Array.isArray(items) ? items : [];

    const currentItems = Array.isArray(items) ? items : [];
    const currentByKey = new Map(
        currentItems.map((item) => [item.documentId ?? item.id, item])
    );
    const seenKeys = new Set();
    const next = [];

    incomingItems.forEach((item) => {
        if (!item) return;
        const normalizedItem = normalizeItem(item);
        const key = normalizedItem.documentId ?? normalizedItem.id;
        seenKeys.add(key);
        const current = currentByKey.get(key);
        next.push(current ? {...current, ...normalizedItem} : normalizedItem);
    });

    currentItems.forEach((item) => {
        const key = item.documentId ?? item.id;
        if (!seenKeys.has(key)) {
            next.push(item);
        }
    });

    return next;
}

function removeByDocumentId(items, item) {
    const key = item?.documentId ?? item?.id;
    return items.filter((current) => (current.documentId ?? current.id) !== key);
}

function collectWorkspaceTasks(workspace) {
    const tasks = [
        ...(Array.isArray(workspace?.tasks) ? workspace.tasks : []),
        ...(Array.isArray(workspace?.projects)
            ? workspace.projects.flatMap((project) => Array.isArray(project?.tasks) ? project.tasks : [])
            : []),
    ];

    return normalizeByDocumentId(tasks, normalizeTask);
}

const workspaceGraphSlice = createSlice({
    name: "workspaceGraph",
    initialState,
    reducers: {
        hydrateWorkspaceSnapshot(state, action) {
            const payload = action.payload || {};
            state.workspaceId = payload.workspaceId ?? state.workspaceId;
            state.workspaceMembership = payload.workspaceMembership ?? state.workspaceMembership;
            state.workspaceMemberships = Array.isArray(payload.workspaceMemberships)
                ? normalizeByDocumentId(payload.workspaceMemberships)
                : state.workspaceMemberships;
            state.tasks = Array.isArray(payload.tasks)
                ? normalizeByDocumentId(payload.tasks, normalizeTask)
                : state.tasks;
            state.projects = Array.isArray(payload.projects)
                ? normalizeByDocumentId(payload.projects)
                : state.projects;
            state.goals = Array.isArray(payload.goals)
                ? normalizeByDocumentId(payload.goals)
                : state.goals;
            state.teams = Array.isArray(payload.teams)
                ? normalizeByDocumentId(payload.teams)
                : state.teams;
        state.customers = Array.isArray(payload.customers)
            ? normalizeByDocumentId(payload.customers)
            : state.customers;
        state.products = Array.isArray(payload.products)
            ? normalizeByDocumentId(payload.products)
            : state.products;
        state.plans = Array.isArray(payload.plans)
            ? normalizeByDocumentId(payload.plans)
            : state.plans;
        state.orders = Array.isArray(payload.orders)
            ? normalizeByDocumentId(payload.orders)
            : state.orders;
        state.invoices = Array.isArray(payload.invoices)
            ? normalizeByDocumentId(payload.invoices)
            : state.invoices;
        state.billingProfiles = Array.isArray(payload.billingProfiles)
            ? normalizeByDocumentId(payload.billingProfiles)
            : state.billingProfiles;
        state.creditWallets = Array.isArray(payload.creditWallets)
            ? normalizeByDocumentId(payload.creditWallets)
            : state.creditWallets;
        state.creditLedgerEntries = Array.isArray(payload.creditLedgerEntries)
            ? normalizeByDocumentId(payload.creditLedgerEntries)
            : state.creditLedgerEntries;
        state.manualPaymentRequests = Array.isArray(payload.manualPaymentRequests)
            ? normalizeByDocumentId(payload.manualPaymentRequests)
            : state.manualPaymentRequests;
        state.promotions = Array.isArray(payload.promotions)
            ? normalizeByDocumentId(payload.promotions)
            : state.promotions;
        state.coupons = Array.isArray(payload.coupons)
            ? normalizeByDocumentId(payload.coupons)
            : state.coupons;
        state.couponRedemptions = Array.isArray(payload.couponRedemptions)
            ? normalizeByDocumentId(payload.couponRedemptions)
            : state.couponRedemptions;
        state.referralPrograms = Array.isArray(payload.referralPrograms)
            ? normalizeByDocumentId(payload.referralPrograms)
            : state.referralPrograms;
        state.referralCodes = Array.isArray(payload.referralCodes)
            ? normalizeByDocumentId(payload.referralCodes)
            : state.referralCodes;
        state.referralAttributions = Array.isArray(payload.referralAttributions)
            ? normalizeByDocumentId(payload.referralAttributions)
            : state.referralAttributions;
        state.referralRewards = Array.isArray(payload.referralRewards)
            ? normalizeByDocumentId(payload.referralRewards)
            : state.referralRewards;
        state.users = payload.users ?? state.users;
        },
        setWorkspaceGraphWorkspaceId(state, action) {
            state.workspaceId = action.payload;
        },
        setWorkspaceGraphMembership(state, action) {
            state.workspaceMembership = action.payload;
        },
        setWorkspaceGraphMemberships(state, action) {
            state.workspaceMemberships = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphTasks(state, action) {
            state.tasks = normalizeByDocumentId(action.payload || [], normalizeTask);
        },
        mergeWorkspaceTasks(state, action) {
            state.tasks = mergeByDocumentId(
                state.tasks,
                Array.isArray(action.payload) ? action.payload : action.payload ? [action.payload] : [],
                normalizeTask
            );
        },
        setWorkspaceGraphProjects(state, action) {
            state.projects = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphGoals(state, action) {
            state.goals = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphTeams(state, action) {
            state.teams = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphCustomers(state, action) {
            state.customers = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphProducts(state, action) {
            state.products = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphPlans(state, action) {
            state.plans = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphOrders(state, action) {
            state.orders = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphInvoices(state, action) {
            state.invoices = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphBillingProfiles(state, action) {
            state.billingProfiles = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphCreditWallets(state, action) {
            state.creditWallets = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphCreditLedgerEntries(state, action) {
            state.creditLedgerEntries = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphManualPaymentRequests(state, action) {
            state.manualPaymentRequests = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphPromotions(state, action) {
            state.promotions = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphCoupons(state, action) {
            state.coupons = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphCouponRedemptions(state, action) {
            state.couponRedemptions = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphReferralPrograms(state, action) {
            state.referralPrograms = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphReferralCodes(state, action) {
            state.referralCodes = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphReferralAttributions(state, action) {
            state.referralAttributions = normalizeByDocumentId(action.payload || []);
        },
        setWorkspaceGraphReferralRewards(state, action) {
            state.referralRewards = normalizeByDocumentId(action.payload || []);
        },
        upsertWorkspaceTask(state, action) {
            state.tasks = upsertByDocumentId(state.tasks, action.payload, normalizeTask);
        },
        upsertWorkspaceProject(state, action) {
            state.projects = upsertByDocumentId(state.projects, action.payload);
        },
        upsertWorkspaceGoal(state, action) {
            state.goals = upsertByDocumentId(state.goals, action.payload);
        },
        upsertWorkspaceTeam(state, action) {
            state.teams = upsertByDocumentId(state.teams, action.payload);
        },
        upsertWorkspaceCustomer(state, action) {
            state.customers = upsertByDocumentId(state.customers, action.payload);
        },
        upsertWorkspaceProduct(state, action) {
            state.products = upsertByDocumentId(state.products, action.payload);
        },
        upsertWorkspacePlan(state, action) {
            state.plans = upsertByDocumentId(state.plans, action.payload);
        },
        upsertWorkspaceOrder(state, action) {
            state.orders = upsertByDocumentId(state.orders, action.payload);
        },
        upsertWorkspaceInvoice(state, action) {
            state.invoices = upsertByDocumentId(state.invoices, action.payload);
        },
        upsertWorkspaceBillingProfile(state, action) {
            state.billingProfiles = upsertByDocumentId(state.billingProfiles, action.payload);
        },
        upsertWorkspaceCreditWallet(state, action) {
            state.creditWallets = upsertByDocumentId(state.creditWallets, action.payload);
        },
        upsertWorkspaceCreditLedgerEntry(state, action) {
            state.creditLedgerEntries = upsertByDocumentId(state.creditLedgerEntries, action.payload);
        },
        upsertWorkspaceManualPaymentRequest(state, action) {
            state.manualPaymentRequests = upsertByDocumentId(state.manualPaymentRequests, action.payload);
        },
        upsertWorkspacePromotion(state, action) {
            state.promotions = upsertByDocumentId(state.promotions, action.payload);
        },
        upsertWorkspaceCoupon(state, action) {
            state.coupons = upsertByDocumentId(state.coupons, action.payload);
        },
        upsertWorkspaceCouponRedemption(state, action) {
            state.couponRedemptions = upsertByDocumentId(state.couponRedemptions, action.payload);
        },
        upsertWorkspaceReferralProgram(state, action) {
            state.referralPrograms = upsertByDocumentId(state.referralPrograms, action.payload);
        },
        upsertWorkspaceReferralCode(state, action) {
            state.referralCodes = upsertByDocumentId(state.referralCodes, action.payload);
        },
        upsertWorkspaceReferralAttribution(state, action) {
            state.referralAttributions = upsertByDocumentId(state.referralAttributions, action.payload);
        },
        upsertWorkspaceReferralReward(state, action) {
            state.referralRewards = upsertByDocumentId(state.referralRewards, action.payload);
        },
        removeWorkspaceTask(state, action) {
            state.tasks = removeByDocumentId(state.tasks, action.payload);
        },
        removeWorkspaceProject(state, action) {
            state.projects = removeByDocumentId(state.projects, action.payload);
        },
        removeWorkspaceGoal(state, action) {
            state.goals = removeByDocumentId(state.goals, action.payload);
        },
        removeWorkspaceTeam(state, action) {
            state.teams = removeByDocumentId(state.teams, action.payload);
        },
        removeWorkspaceCustomer(state, action) {
            state.customers = removeByDocumentId(state.customers, action.payload);
        },
        removeWorkspaceProduct(state, action) {
            state.products = removeByDocumentId(state.products, action.payload);
        },
        removeWorkspacePlan(state, action) {
            state.plans = removeByDocumentId(state.plans, action.payload);
        },
        removeWorkspaceOrder(state, action) {
            state.orders = removeByDocumentId(state.orders, action.payload);
        },
        removeWorkspaceInvoice(state, action) {
            state.invoices = removeByDocumentId(state.invoices, action.payload);
        },
        removeWorkspaceBillingProfile(state, action) {
            state.billingProfiles = removeByDocumentId(state.billingProfiles, action.payload);
        },
        removeWorkspaceCreditWallet(state, action) {
            state.creditWallets = removeByDocumentId(state.creditWallets, action.payload);
        },
        removeWorkspaceCreditLedgerEntry(state, action) {
            state.creditLedgerEntries = removeByDocumentId(state.creditLedgerEntries, action.payload);
        },
        removeWorkspaceManualPaymentRequest(state, action) {
            state.manualPaymentRequests = removeByDocumentId(state.manualPaymentRequests, action.payload);
        },
        removeWorkspacePromotion(state, action) {
            state.promotions = removeByDocumentId(state.promotions, action.payload);
        },
        removeWorkspaceCoupon(state, action) {
            state.coupons = removeByDocumentId(state.coupons, action.payload);
        },
        removeWorkspaceCouponRedemption(state, action) {
            state.couponRedemptions = removeByDocumentId(state.couponRedemptions, action.payload);
        },
        removeWorkspaceReferralProgram(state, action) {
            state.referralPrograms = removeByDocumentId(state.referralPrograms, action.payload);
        },
        removeWorkspaceReferralCode(state, action) {
            state.referralCodes = removeByDocumentId(state.referralCodes, action.payload);
        },
        removeWorkspaceReferralAttribution(state, action) {
            state.referralAttributions = removeByDocumentId(state.referralAttributions, action.payload);
        },
        removeWorkspaceReferralReward(state, action) {
            state.referralRewards = removeByDocumentId(state.referralRewards, action.payload);
        },
        applyWorkspaceRealtimeEvent(state, action) {
            state.lastEvent = action.payload;
        },
    }
});

export const {
    hydrateWorkspaceSnapshot,
    setWorkspaceGraphWorkspaceId,
    setWorkspaceGraphMembership,
    setWorkspaceGraphMemberships,
    setWorkspaceGraphTasks,
    mergeWorkspaceTasks,
    setWorkspaceGraphProjects,
    setWorkspaceGraphGoals,
    setWorkspaceGraphTeams,
    setWorkspaceGraphCustomers,
    setWorkspaceGraphProducts,
    setWorkspaceGraphPlans,
    setWorkspaceGraphOrders,
    setWorkspaceGraphInvoices,
    setWorkspaceGraphBillingProfiles,
    setWorkspaceGraphCreditWallets,
    setWorkspaceGraphCreditLedgerEntries,
    setWorkspaceGraphManualPaymentRequests,
    setWorkspaceGraphPromotions,
    setWorkspaceGraphCoupons,
    setWorkspaceGraphCouponRedemptions,
    setWorkspaceGraphReferralPrograms,
    setWorkspaceGraphReferralCodes,
    setWorkspaceGraphReferralAttributions,
    setWorkspaceGraphReferralRewards,
    upsertWorkspaceTask,
    upsertWorkspaceProject,
    upsertWorkspaceGoal,
    upsertWorkspaceTeam,
    upsertWorkspaceCustomer,
    upsertWorkspaceProduct,
    upsertWorkspacePlan,
    upsertWorkspaceOrder,
    upsertWorkspaceInvoice,
    upsertWorkspaceBillingProfile,
    upsertWorkspaceCreditWallet,
    upsertWorkspaceCreditLedgerEntry,
    upsertWorkspaceManualPaymentRequest,
    upsertWorkspacePromotion,
    upsertWorkspaceCoupon,
    upsertWorkspaceCouponRedemption,
    upsertWorkspaceReferralProgram,
    upsertWorkspaceReferralCode,
    upsertWorkspaceReferralAttribution,
    upsertWorkspaceReferralReward,
    removeWorkspaceTask,
    removeWorkspaceProject,
    removeWorkspaceGoal,
    removeWorkspaceTeam,
    removeWorkspaceCustomer,
    removeWorkspaceProduct,
    removeWorkspacePlan,
    removeWorkspaceOrder,
    removeWorkspaceInvoice,
    removeWorkspaceBillingProfile,
    removeWorkspaceCreditWallet,
    removeWorkspaceCreditLedgerEntry,
    removeWorkspaceManualPaymentRequest,
    removeWorkspacePromotion,
    removeWorkspaceCoupon,
    removeWorkspaceCouponRedemption,
    removeWorkspaceReferralProgram,
    removeWorkspaceReferralCode,
    removeWorkspaceReferralAttribution,
    removeWorkspaceReferralReward,
    applyWorkspaceRealtimeEvent,
} = workspaceGraphSlice.actions;

export const workspaceGraphSelectors = {
    tasks: (state) => state.workspaceGraph.tasks,
    projects: (state) => state.workspaceGraph.projects,
    goals: (state) => state.workspaceGraph.goals,
    teams: (state) => state.workspaceGraph.teams,
    customers: (state) => state.workspaceGraph.customers,
    products: (state) => state.workspaceGraph.products,
    plans: (state) => state.workspaceGraph.plans,
    orders: (state) => state.workspaceGraph.orders,
    invoices: (state) => state.workspaceGraph.invoices,
    billingProfiles: (state) => state.workspaceGraph.billingProfiles,
    creditWallets: (state) => state.workspaceGraph.creditWallets,
    creditLedgerEntries: (state) => state.workspaceGraph.creditLedgerEntries,
    manualPaymentRequests: (state) => state.workspaceGraph.manualPaymentRequests,
    promotions: (state) => state.workspaceGraph.promotions,
    coupons: (state) => state.workspaceGraph.coupons,
    couponRedemptions: (state) => state.workspaceGraph.couponRedemptions,
    referralPrograms: (state) => state.workspaceGraph.referralPrograms,
    referralCodes: (state) => state.workspaceGraph.referralCodes,
    referralAttributions: (state) => state.workspaceGraph.referralAttributions,
    referralRewards: (state) => state.workspaceGraph.referralRewards,
    workspaceMembership: (state) => state.workspaceGraph.workspaceMembership,
    workspaceMemberships: (state) => state.workspaceGraph.workspaceMemberships,
    workspaceId: (state) => state.workspaceGraph.workspaceId,
};

export { collectWorkspaceTasks };

export default workspaceGraphSlice.reducer;
